import { useCallback, useRef } from 'react'
import { useChatStore } from '../stores/chatStore'
import { getChatHistory, getPreviousSession } from '../api/chat'

export function useInfiniteHistory() {
  const {
    messages,
    sessionId,
    isLoadingHistory,
    hasMoreHistory,
    currentTherapist,
    prependMessages,
    setLoadingHistory,
    setHasMoreHistory,
    addSessionMeta,
  } = useChatStore()

  const fetchingRef = useRef(false)

  const loadOlderMessages = useCallback(async () => {
    // Don't load until therapist is known — prevents unfiltered cross-therapist loading
    if (fetchingRef.current || isLoadingHistory || !hasMoreHistory || !currentTherapist) return
    fetchingRef.current = true
    setLoadingHistory(true)

    try {
      // Find oldest message with a real server ID (positive)
      const oldestMsg = messages.find(m => m.id > 0)
      if (!oldestMsg) {
        setHasMoreHistory(false)
        return
      }

      const targetSessionId = oldestMsg.sessionId || sessionId
      if (!targetSessionId) {
        setHasMoreHistory(false)
        return
      }

      // Load older messages from the same session
      const result = await getChatHistory(targetSessionId, 50, oldestMsg.id)

      if (result.messages.length > 0) {
        prependMessages(result.messages)
      }

      if (!result.has_more || result.messages.length === 0) {
        // Current session exhausted — find next non-empty previous session.
        // Skip empty sessions in a loop to avoid infinite re-triggers
        // (Telegram users may have many bot-created sessions with 0 visible messages).
        let cursorId = targetSessionId
        const MAX_SKIPS = 10
        let found = false

        for (let i = 0; i < MAX_SKIPS; i++) {
          const prevSession = await getPreviousSession(cursorId, currentTherapist?.type)
          if (!prevSession) {
            setHasMoreHistory(false)
            found = true
            break
          }

          addSessionMeta(prevSession.id, {
            therapist: prevSession.therapist,
            startedAt: prevSession.started_at,
          })

          if (prevSession.message_count === 0) {
            cursorId = prevSession.id
            continue // Skip empty session, try the one before it
          }

          const prevResult = await getChatHistory(prevSession.id, 50)
          if (prevResult.messages.length > 0) {
            prependMessages(prevResult.messages)
            setHasMoreHistory(true) // Might have even older sessions
            found = true
            break
          }

          // All messages were system/filtered out — skip this session too
          cursorId = prevSession.id
        }

        if (!found) {
          // Exhausted max skips without finding content or reaching end
          setHasMoreHistory(false)
        }
      }
    } catch {
      // Silently fail — user can retry by scrolling up again
    } finally {
      setLoadingHistory(false)
      fetchingRef.current = false
    }
  }, [messages, sessionId, isLoadingHistory, hasMoreHistory,
      currentTherapist, prependMessages, setLoadingHistory, setHasMoreHistory, addSessionMeta])

  return { loadOlderMessages, isLoadingHistory, hasMoreHistory }
}
